import React from 'react';
import { SeoHero } from '@/components/ui/seo-hero';
import { Badge } from '@/components/ui/badge';
import { ClientLogosSlider } from '@/components/ui/ClientLogosSlider';
import SeoProcessSection from '@/components/SeoProcessSection';
import MarketingCampaignsTestimonialsSection from '@/components/MarketingCampaignsTestimonialsSection';
import SeoGettingStartedSection from '@/components/SeoGettingStartedSection';
import SeoFaqSection from '@/components/SeoFaqSection';
import FinalCTA from '@/components/FinalCTA';
import Navigation from '@/components/Navigation';
import FooterWithNewsletter from '@/components/ui/footer-column';
import SEO from '@/components/SEO';
import { useSEO } from '@/hooks/useSEO';
import { getSEOConfigForPage } from '@/utils/seoUtils';
import { Sparkles, Shield, BookOpen, Palette, Rocket, Users, Globe } from 'lucide-react';
import marketingCampaignsHero from '@/assets/marketing-campaigns-hero.jpg';

const LaunchMarketingCampaignsPage = () => {
  const { fallbackMetadata } = getSEOConfigForPage('/launch-marketing-campaigns-with-ai');
  const { seoData, canonicalUrl, noindex, nofollow } = useSEO({ fallbackMetadata });

  const marketingCampaignSteps = [
    {
      icon: Sparkles,
      title: "Spark ideas instantly with Magic Prompt",
      description: "Enter a campaign theme or just a few words—Magic Prompt expands it into channel-ready copy, creative concepts, and visuals in seconds."
    },
    {
      icon: Shield,
      title: "Lock in your brand with Brand Vault",
      description: "Activate your Brand Vault to auto-apply tone, value propositions, target audiences, and visual rules. Every asset, from headlines to hashtags, is consistently you."
    },
    {
      icon: BookOpen,
      title: "Automate campaigns with Playbooks",
      description: "Pick a Playbook to generate full multi-channel campaigns. Playbooks stack tasks (like captions + ads + emails) into one workflow, saving you from repetitive setup."
    },
    {
      icon: Palette,
      title: "Edit and launch everywhere",
      description: "Use the Visual Editor to tweak layouts and add text. Save/download your result and your campaign is ready to be taken live on your preferred platforms."
    }
  ];

  const marketingGettingStartedSteps = [
    {
      icon: Rocket,
      title: "Start your campaign",
      description: "Open gimmefy, create your campaign, set your Brand Vault, and enter a prompt or goal. Templates and smart suggestions get you moving fast.",
      time: "15 minutes",
      gradient: "bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30",
      border: "border-blue-200/50 dark:border-blue-800/50",
      numberBg: "bg-blue-100 dark:bg-blue-900/50",
      numberBorder: "border-blue-300/50 dark:border-blue-700/50",
      numberText: "text-blue-700 dark:text-blue-300",
      timeBg: "bg-blue-600 dark:bg-blue-500",
      timeText: "text-white"
    },
    {
      icon: Users,
      title: "Customise and collaborate", 
      description: "Invite your team, and use Playbooks to automate content workflows.",
      time: "5 minutes",
      gradient: "bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30",
      border: "border-emerald-200/50 dark:border-emerald-800/50",
      numberBg: "bg-emerald-100 dark:bg-emerald-900/50",
      numberBorder: "border-emerald-300/50 dark:border-emerald-700/50",
      numberText: "text-emerald-700 dark:text-emerald-300",
      timeBg: "bg-emerald-600 dark:bg-emerald-500",
      timeText: "text-white"
    },
    {
      icon: Globe,
      title: "Launch across all channels",
      description: "Edit copy and visuals as needed, then export or publish through your preferred tools; with all the content saved in your gimmefy library for future use.",
      time: "varies",
      gradient: "bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/30",
      border: "border-orange-200/50 dark:border-orange-800/50",
      numberBg: "bg-orange-100 dark:bg-orange-900/50",
      numberBorder: "border-orange-300/50 dark:border-orange-700/50",
      numberText: "text-orange-700 dark:text-orange-300",
      timeBg: "bg-orange-600 dark:bg-orange-500",
      timeText: "text-white"
    }
  ];

  const marketingCampaignsFaqs = [
    {
      question: "How fast can I go from idea to campaign with gimmefy.ai?",
      answer: "Most users create a full set of campaign assets—including posts for every major channel—in under 15 minutes."
    },
    {
      question: "Can my whole team collaborate on campaigns?",
      answer: "Yes. Team plan subscription allows organizations to Invite users, assign roles, and manage every edit or review in one workspace—no endless email loops."
    },
    {
      question: "Will my campaigns always match our brand guidelines?",
      answer: "Yes. Brand Vault rules are applied to every asset, so tone and visuals stay consistent."
    },
    {
      question: "Does gimmefy really automate all campaign steps?",
      answer: "Playbooks automate content creation workflows, turning multi-step processes into single clicks. You can export or connect to your scheduling tools for deployment."
    },
    {
      question: "Can I launch campaigns to multiple channels at once?",
      answer: "Yes. gimmefy produces assets tailored for platforms like Facebook, Instagram, LinkedIn, TikTok, X, and email. From there, export directly or plug into your publishing stack."
    },
    {
      question: "Is campaign analytics included?",
      answer: "gimmefy focuses on content creation. For performance tracking, export your campaigns into scheduling or analytics tools where reporting is built-in."
    },
    {
      question: "Are there ready-made campaign templates?",
      answer: "No, but you can create your Playbooks and customise them. Bundle research, planning, and ideation tasks together, and create repeatable campaign workflows."
    },
    {
      question: "How does gimmefy help with compliance?",
      answer: "Brand Vault and approval flows help teams check content against internal rules and guidelines before export."
    },
    {
      question: "What support is available if I get stuck?",
      answer: "You'll find onboarding resources, a help library, and direct support when needed."
    },
    {
      question: "Can I reuse successful campaigns quickly?",
      answer: "Yes. Duplicate past campaigns, update details, and spin them into fresh assets—perfect for A/B testing or recurring activations."
    }
  ];

  return (
    <>
      {seoData && (
        <SEO 
          data={seoData} 
          canonicalUrl={canonicalUrl}
          noindex={noindex}
          nofollow={nofollow}
        />
      )}
      
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <SeoHero
          badge={
            <Badge className="bg-primary/10 text-primary border-primary/20 text-lg px-6 py-3 rounded-full font-medium shadow-lg backdrop-blur-sm">
              🚀 AI-Powered Marketing Campaigns That Convert
            </Badge>
          }
          mainTitle="Launch Marketing Campaigns with AI"
          title="Roll-out 360-degree campaigns in minutes"
          subtitle="Instant campaigns. Next-level speed. Total brand control. Create campaign-ready assets across every channel with gimmefy's marketing-specialist AI; so you move faster and stay consistent."
          primaryCta={{
            label: "Start for FREE",
            href: "/signup/contentcal"
          }}
          image={{
            src: marketingCampaignsHero,
            alt: "Business team meeting with marketing campaign planning materials, laptops showing analytics, and promotional products in a modern office setting"
          }}
        />

        <ClientLogosSlider />
        
        <SeoProcessSection 
          title="How it works: campaign creation in four steps"
          subtitle="Follow these simple steps to create and launch marketing campaigns with AI"
          steps={marketingCampaignSteps}
        />
        
        <MarketingCampaignsTestimonialsSection />
        
        <SeoGettingStartedSection 
          title="Get started in 3 simple steps"
          steps={marketingGettingStartedSteps}
        />
        
        <FinalCTA 
          title="Experience the platform built for <br/> speed, control, and campaign impact." 
          subtitle="Your next big launch is minutes away." 
          signupUrl="/signup/contentcal"
        />
        
        <SeoFaqSection faqs={marketingCampaignsFaqs} />
        
        <FooterWithNewsletter />
      </div>
    </>
  );
};

export default LaunchMarketingCampaignsPage;